import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const SCHED_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const schedHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
import { useLocation } from '../context/LocationContext';
import { Layout } from '../components/Layout';
import { scheduleAPI, staffAPI, availabilityAPI, jobRolesAPI, shiftTemplatesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { getWeekDates, getShortDayName, formatDate } from '../lib/utils';
import { PrintableSchedule } from '../components/PrintableSchedule';
import { AddShiftDialog } from '../components/schedule/AddShiftDialog';
import { TemplatesSidebar } from '../components/schedule/TemplatesSidebar';
import { PublishDialog } from '../components/schedule/PublishDialog';
import { BulkCreateDialog } from '../components/schedule/BulkCreateDialog';
import { RolesDialog } from '../components/schedule/RolesDialog';
import { ShiftTemplatesDialog } from '../components/schedule/ShiftTemplatesDialog';
import { CopyLastWeekDialog } from '../components/schedule/CopyLastWeekDialog';
import {
  Calendar,
  Plus,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Trash2,
  UserCheck,
  AlertTriangle,
  Clock,
  User,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  Download,
  FileText,
  FileImage,
  GripVertical,
  Palette,
  Settings2,
  Printer,
  Send,
  Users,
  CalendarPlus,
  Bell,
  Copy,
  BookmarkPlus,
  FolderOpen,
} from 'lucide-react';
import { holidayAPI, leaveAPI } from '../lib/api';
import { ReplacementDialog } from '../components/schedule/ReplacementDialog';
import { HelpTooltip } from '../components/HelpTooltip';

export default function Schedule() {
  const { user, canManage } = useAuth();
  const { selectedLocationId, isAllLocations, selectedLocation } = useLocation();
  const activeLocId = isAllLocations ? user?.restaurant_id : selectedLocationId;
  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [staff, setStaff] = useState([]);
  const [staffAvailability, setStaffAvailability] = useState([]);
  const [showAvailability, setShowAvailability] = useState(true);
  const [exporting, setExporting] = useState(false);
  const exportRef = useRef(null);
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(today.setDate(diff));
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [formData, setFormData] = useState({
    user_id: '',
    start_time: '09:00',
    end_time: '17:00',
    role_id: '',
    template_id: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  // Job Roles & Shift Templates state
  const [jobRoles, setJobRoles] = useState([]);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [showTemplatePanel, setShowTemplatePanel] = useState(true);
  const [draggingTemplate, setDraggingTemplate] = useState(null);
  const [rolesDialogOpen, setRolesDialogOpen] = useState(false);
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);
  const [roleFormData, setRoleFormData] = useState({ name: '', description: '', color: '#3b82f6' });
  const [templateFormData, setTemplateFormData] = useState({ 
    name: '', role_id: '', start_time: '09:00', end_time: '17:00', break_duration: '', label: '' 
  });
  const [editingRole, setEditingRole] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);

  // Weekly schedule templates state
  const [weeklyTemplates, setWeeklyTemplates]   = useState([]);
  const [saveTplOpen, setSaveTplOpen]           = useState(false);
  const [saveTplName, setSaveTplName]           = useState('');
  const [savingTpl, setSavingTpl]               = useState(false);
  const [loadTplOpen, setLoadTplOpen]           = useState(false);
  const [loadingTpl, setLoadingTpl]             = useState(null);

  // Schedule publishing state
  const [draftCount, setDraftCount] = useState(0);
  const [publishedCount, setPublishedCount] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [sendNotifications, setSendNotifications] = useState(true);

  // Bulk creation state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFormData, setBulkFormData] = useState({
    template_id: '',
    user_ids: [],
    dates: [],
    is_draft: true,
  });
  const [creatingBulk, setCreatingBulk] = useState(false);

  // Print view state
  const [printDialogOpen, setPrintDialogOpen] = useState(false);

  // Copy last week state
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyingLastWeek, setCopyingLastWeek] = useState(false);

  const weekDates = getWeekDates(new Date(weekStart));

  // Holiday highlighting for the visible week (Phase 3 — visual only).
  // We key on weekStart ISO so we re-fetch once per week change, never per
  // render. Resolver responses are TTL-cached server-side.
  const [holidaysByDate, setHolidaysByDate] = useState({});
  const weekKey = weekDates[0]?.toISOString().split('T')[0] || '';
  useEffect(() => {
    if (!weekDates.length) return;
    let cancelled = false;
    const isos = weekDates.map((d) => d.toISOString().split('T')[0]);
    holidayAPI
      .resolveBatch(isos, activeLocId || undefined)
      .then((m) => { if (!cancelled) setHolidaysByDate(m); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekKey, activeLocId]);

  // Leave overlay for the visible week — pure visual layer. We fetch
  // approved + pending leave that overlaps the visible 7-day range using
  // the existing /leave-requests/calendar endpoint (tenant-isolated, indexed).
  // Keyed on weekStart + activeLocId, so it never re-renders during shift
  // edits. Errors are silently ignored: leave data is decorative only.
  const [leaveByDate, setLeaveByDate] = useState({});
  const [replacementShiftId, setReplacementShiftId] = useState(null);
  useEffect(() => {
    if (!weekDates.length) return;
    let cancelled = false;
    const from = weekDates[0].toISOString().split('T')[0];
    const to = weekDates[weekDates.length - 1].toISOString().split('T')[0];
    const params = { start: from, end: to };
    if (activeLocId) params.location_id = activeLocId;
    leaveAPI
      .calendar(params)
      .then((r) => {
        if (cancelled) return;
        // Expand each request across its date range into a dict keyed by ISO.
        const map = {};
        (r.data?.items || []).forEach((it) => {
          if (!it.start_date || !it.end_date) return;
          const start = new Date(it.start_date);
          const end = new Date(it.end_date);
          for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const k = d.toISOString().split('T')[0];
            (map[k] = map[k] || []).push(it);
          }
        });
        setLeaveByDate(map);
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekKey, activeLocId]);

  // Fetch job roles and shift templates
  const fetchRolesAndTemplates = useCallback(async () => {
    if (!canManage) return;
    try {
      const [rolesRes, templatesRes] = await Promise.all([
        jobRolesAPI.getAll(),
        shiftTemplatesAPI.getAll(),
      ]);
      setJobRoles(rolesRes.data.roles || []);
      setShiftTemplates(templatesRes.data.templates || []);
    } catch (error) {
      console.error('Error fetching roles/templates:', error);
    }
  }, [canManage]);

  const fetchData = useCallback(async () => {
    if (!activeLocId) {
      setLoading(false);
      return;
    }

    try {
      const endDate = new Date(weekStart);
      endDate.setDate(endDate.getDate() + 6);

      const promises = [
        scheduleAPI.getAll(activeLocId, {
          start_date: weekStart.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0],
        }),
      ];

      if (canManage) {
        promises.push(staffAPI.getAll(activeLocId));
        promises.push(availabilityAPI.getAllForRestaurant(activeLocId));
        promises.push(scheduleAPI.getDraftCount(activeLocId, weekStart.toISOString().split('T')[0]));
      }

      const results = await Promise.all(promises);

      setSchedules(results[0].data);
      if (canManage) {
        setStaff(results[1].data);
        setStaffAvailability(results[2].data);
        if (results[3]) {
          setDraftCount(results[3].data.draft_count || 0);
          setPublishedCount(results[3].data.published_count || 0);
        }
      }
    } catch (error) {
      console.error('Error fetching schedule data:', error);
    } finally {
      setLoading(false);
    }
  }, [user, weekStart, canManage, activeLocId]);

  useEffect(() => {
    fetchData();
    fetchRolesAndTemplates();
    if (canManage) fetchWeeklyTemplates();
  }, [fetchData, fetchRolesAndTemplates]);

  // Get availability for a specific staff member on a specific day
  const getStaffAvailabilityForDay = useCallback((userId, dayOfWeek) => {
    const staffData = staffAvailability.find(s => s.user_id === userId);
    if (!staffData || !staffData.availability) return null;
    return staffData.availability.find(a => a.day_of_week === dayOfWeek);
  }, [staffAvailability]);

  // Check if a time range conflicts with availability
  const checkAvailabilityConflict = useCallback((userId, date, startTime, endTime) => {
    const dayOfWeek = date.getDay();
    const availability = getStaffAvailabilityForDay(userId, dayOfWeek);
    
    if (!availability) {
      return { hasConflict: false, reason: 'No availability set' };
    }
    
    if (!availability.is_available) {
      return { hasConflict: true, reason: 'Staff marked as unavailable this day' };
    }
    
    if (availability.start_time && availability.end_time) {
      const shiftStart = startTime;
      const shiftEnd = endTime;
      const availStart = availability.start_time;
      const availEnd = availability.end_time;
      
      if (shiftStart < availStart || shiftEnd > availEnd) {
        return { 
          hasConflict: true, 
          reason: `Outside availability (${availStart} - ${availEnd})` 
        };
      }
    }
    
    return { hasConflict: false, reason: null };
  }, [getStaffAvailabilityForDay]);

  // Check for double booking
  const checkDoubleBooking = useCallback((userId, date, startTime, endTime, excludeScheduleId = null) => {
    const dateStr = date.toISOString().split('T')[0];
    const existingShifts = schedules.filter(s => 
      s.user_id === userId && 
      s.date === dateStr &&
      s.id !== excludeScheduleId
    );
    
    for (const shift of existingShifts) {
      // Check for time overlap
      if (
        (startTime >= shift.start_time && startTime < shift.end_time) ||
        (endTime > shift.start_time && endTime <= shift.end_time) ||
        (startTime <= shift.start_time && endTime >= shift.end_time)
      ) {
        return { 
          hasConflict: true, 
          reason: `Overlaps with existing shift (${shift.start_time} - ${shift.end_time})` 
        };
      }
    }
    
    return { hasConflict: false, reason: null };
  }, [schedules]);

  // Validate shift when form data changes
  useEffect(() => {
    if (!formData.user_id || !selectedDate) {
      setConflicts([]);
      return;
    }

    const newConflicts = [];
    
    // Check availability conflict
    const availConflict = checkAvailabilityConflict(
      formData.user_id, 
      selectedDate, 
      formData.start_time, 
      formData.end_time
    );
    if (availConflict.hasConflict) {
      newConflicts.push({ type: 'availability', ...availConflict });
    }
    
    // Check double booking
    const doubleBooking = checkDoubleBooking(
      formData.user_id,
      selectedDate,
      formData.start_time,
      formData.end_time
    );
    if (doubleBooking.hasConflict) {
      newConflicts.push({ type: 'double_booking', ...doubleBooking });
    }
    
    setConflicts(newConflicts);
  }, [formData, selectedDate, checkAvailabilityConflict, checkDoubleBooking]);

  const handlePrevWeek = () => {
    const newDate = new Date(weekStart);
    newDate.setDate(newDate.getDate() - 7);
    setWeekStart(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(weekStart);
    newDate.setDate(newDate.getDate() + 7);
    setWeekStart(newDate);
  };

  const handleAddShift = (date, userId = '') => {
    setSelectedDate(date);
    setFormData({ user_id: userId, start_time: '09:00', end_time: '17:00', role_id: '', template_id: '' });
    setConflicts([]);
    setDialogOpen(true);
  };

  // Handle template drag-drop
  const handleTemplateDrop = async (template, date, userId) => {
    if (!activeLocId) return;
    try {
      await scheduleAPI.create(activeLocId, {
        user_id: userId,
        date: date.toISOString().split('T')[0],
        start_time: template.start_time,
        end_time: template.end_time,
        role_id: template.role_id,
        template_id: template.id,
        break_duration: template.break_duration,
      });
      toast.success(`Shift created from "${template.name}" template`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shift');
    }
  };

  const handleSubmit = async (e, enhancedFormData = null) => {
    e.preventDefault();
    
    const dataToSubmit = enhancedFormData || formData;
    
    // Warn about conflicts but allow override
    if (conflicts.length > 0) {
      const confirmProceed = window.confirm(
        `Warning: This shift has ${conflicts.length} conflict(s):\n\n` +
        conflicts.map(c => `• ${c.reason}`).join('\n') +
        '\n\nDo you want to schedule anyway?'
      );
      if (!confirmProceed) return;
    }
    
    setSubmitting(true);

    try {
      await scheduleAPI.create(activeLocId, {
        user_id: dataToSubmit.user_id,
        date: selectedDate.toISOString().split('T')[0],
        start_time: dataToSubmit.start_time,
        end_time: dataToSubmit.end_time,
        role_id: dataToSubmit.role_id || undefined,
        template_id: dataToSubmit.template_id || undefined,
        notify_staff: dataToSubmit.notify_staff !== undefined ? dataToSubmit.notify_staff : true,
      });
      toast.success('Shift created successfully');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shift');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteShift = async (scheduleId) => {
    if (!confirm('Remove this shift?')) return;

    try {
      await scheduleAPI.delete(scheduleId);
      toast.success('Shift removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove shift');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // JOB ROLES MANAGEMENT
  // ─────────────────────────────────────────────────────────────────────────
  const handleSaveRole = async () => {
    if (!roleFormData.name.trim()) {
      toast.error('Role name is required');
      return;
    }
    try {
      if (editingRole) {
        await jobRolesAPI.update(editingRole.id, roleFormData);
        toast.success('Role updated');
      } else {
        await jobRolesAPI.create(roleFormData);
        toast.success('Role created');
      }
      setRoleFormData({ name: '', description: '', color: '#3b82f6' });
      setEditingRole(null);
      fetchRolesAndTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!confirm('Delete this role?')) return;
    try {
      await jobRolesAPI.delete(roleId);
      toast.success('Role deleted');
      fetchRolesAndTemplates();
    } catch (error) {
      toast.error('Failed to delete role');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // SHIFT TEMPLATES MANAGEMENT
  // ─────────────────────────────────────────────────────────────────────────
  const handleSaveTemplate = async () => {
    if (!templateFormData.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (!templateFormData.start_time || !templateFormData.end_time) {
      toast.error('Start and end times are required');
      return;
    }
    try {
      const payload = {
        ...templateFormData,
        break_duration: templateFormData.break_duration ? parseInt(templateFormData.break_duration) : null,
      };
      if (editingTemplate) {
        await shiftTemplatesAPI.update(editingTemplate.id, payload);
        toast.success('Template updated');
      } else {
        await shiftTemplatesAPI.create(payload);
        toast.success('Template created');
      }
      setTemplateFormData({ name: '', role_id: '', start_time: '09:00', end_time: '17:00', break_duration: '', label: '' });
      setEditingTemplate(null);
      fetchRolesAndTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Delete this template?')) return;
    try {
      await shiftTemplatesAPI.delete(templateId);
      toast.success('Template deleted');
      fetchRolesAndTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // SCHEDULE PUBLISHING
  // ─────────────────────────────────────────────────────────────────────────
  const handlePublishSchedule = async () => {
    if (!activeLocId) return;
    setPublishing(true);
    try {
      const result = await scheduleAPI.publish(activeLocId, {
        week_start: weekStart.toISOString().split('T')[0],
        send_notifications: sendNotifications,
      });
      toast.success(result.data.message || `Published ${result.data.published_count} shifts`);
      setPublishDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish schedule');
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublishSchedule = async () => {
    if (!activeLocId) return;
    try {
      const result = await scheduleAPI.unpublish(activeLocId, {
        week_start: weekStart.toISOString().split('T')[0],
      });
      fetchData();
      toast.success(result.data.unpublished_count > 0
        ? `Unpublished ${result.data.unpublished_count} shifts`
        : 'No published shifts this week');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unpublish failed');
    }
  };

  const fetchWeeklyTemplates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${SCHED_BASE}/schedule/templates`, { headers: schedHeaders() });
      setWeeklyTemplates(data);
    } catch (err) {
      console.error('Failed to load weekly templates', err);
    }
  }, []);

  const handleSaveWeekAsTemplate = async () => {
    if (!activeLocId || !saveTplName.trim()) return;
    setSavingTpl(true);
    try {
      await axios.post(`${SCHED_BASE}/schedule/templates/save`, {
        week_start:    weekStart.toISOString().split('T')[0],
        restaurant_id: activeLocId,
        name:          saveTplName.trim(),
      }, { headers: schedHeaders() });
      toast.success('Week saved as template');
      setSaveTplOpen(false);
      setSaveTplName('');
      fetchWeeklyTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSavingTpl(false); }
  };

  const handleLoadWeekTemplate = async (templateId) => {
    if (!activeLocId) return;
    setLoadingTpl(templateId);
    try {
      const res = await axios.post(`${SCHED_BASE}/schedule/templates/load`, {
        template_id:   templateId,
        week_start:    weekStart.toISOString().split('T')[0],
        restaurant_id: activeLocId,
      }, { headers: schedHeaders() });
      toast.success(`Loaded ${res.data.created_count} shifts from template`);
      setLoadTplOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Load failed');
    } finally { setLoadingTpl(null); }
  };

  const handleDeleteWeekTemplate = async (templateId) => {
    try {
      await axios.delete(`${SCHED_BASE}/schedule/templates/${templateId}`, { headers: schedHeaders() });
      setWeeklyTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success('Template deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // BULK SHIFT CREATION
  // ─────────────────────────────────────────────────────────────────────────
  const handleOpenBulkDialog = () => {
    setBulkFormData({
      template_id: shiftTemplates[0]?.id || '',
      user_ids: [],
      dates: [],
      is_draft: true,
    });
    setBulkDialogOpen(true);
  };

  const handleBulkCreate = async () => {
    if (!bulkFormData.template_id) {
      toast.error('Please select a template');
      return;
    }
    if (bulkFormData.user_ids.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }
    if (bulkFormData.dates.length === 0) {
      toast.error('Please select at least one date');
      return;
    }
    
    setCreatingBulk(true);
    try {
      const result = await scheduleAPI.createBulk(activeLocId, bulkFormData);
      toast.success(result.data.message || `Created ${result.data.created_count} shifts`);

      const skippedCount = result.data?.skipped_absence?.length || 0;
      if (skippedCount > 0) {
        toast.warning(
          `${skippedCount} ${skippedCount === 1 ? 'shift' : 'shifts'} skipped due to approved time off`
        );
      }

      setBulkDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shifts');
    } finally {
      setCreatingBulk(false);
    }
  };

  const toggleBulkUser = (userId) => {
    setBulkFormData(prev => ({
      ...prev,
      user_ids: prev.user_ids.includes(userId)
        ? prev.user_ids.filter(id => id !== userId)
        : [...prev.user_ids, userId]
    }));
  };

  const toggleBulkDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    setBulkFormData(prev => ({
      ...prev,
      dates: prev.dates.includes(dateStr)
        ? prev.dates.filter(d => d !== dateStr)
        : [...prev.dates, dateStr]
    }));
  };

  const selectAllWeekDates = () => {
    const allDates = weekDates.map(d => d.toISOString().split('T')[0]);
    setBulkFormData(prev => ({ ...prev, dates: allDates }));
  };

  const selectAllStaff = () => {
    setBulkFormData(prev => ({ ...prev, user_ids: staff.map(s => s.id) }));
  };

  // ─────────────────────────────────────────────────────────────────────────
  // COPY LAST WEEK
  // ─────────────────────────────────────────────────────────────────────────
  const handleCopyLastWeek = async () => {
    if (!activeLocId) return;
    setCopyingLastWeek(true);
    try {
      const prevWeekStart = new Date(weekStart);
      prevWeekStart.setDate(prevWeekStart.getDate() - 7);
      const prevWeekEnd = new Date(prevWeekStart);
      prevWeekEnd.setDate(prevWeekEnd.getDate() + 6);

      const { data: prevShifts } = await scheduleAPI.getAll(activeLocId, {
        start_date: prevWeekStart.toISOString().split('T')[0],
        end_date: prevWeekEnd.toISOString().split('T')[0],
      });

      if (!prevShifts || prevShifts.length === 0) {
        toast.info('No shifts found in the previous week to copy');
        setCopyDialogOpen(false);
        return;
      }

      const results = await Promise.allSettled(
        prevShifts.map((shift) => {
          // Parse as UTC noon to avoid timezone date-shift issues
          const newDate = new Date(shift.date + 'T12:00:00Z');
          newDate.setUTCDate(newDate.getUTCDate() + 7);
          return scheduleAPI.create(activeLocId, {
            user_id: shift.user_id,
            date: newDate.toISOString().split('T')[0],
            start_time: shift.start_time,
            end_time: shift.end_time,
            ...(shift.role_id && { role_id: shift.role_id }),
            ...(shift.template_id && { template_id: shift.template_id }),
            ...(shift.break_duration && { break_duration: shift.break_duration }),
            is_draft: true,
          });
        })
      );

      const created = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      if (created > 0) {
        toast.success(
          `Copied ${created} shift${created !== 1 ? 's' : ''} as drafts` +
          (failed > 0 ? ` (${failed} skipped)` : '')
        );
      } else {
        toast.error('Could not copy any shifts');
      }

      setCopyDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to copy last week\'s schedule');
    } finally {
      setCopyingLastWeek(false);
    }
  };

  // Get role by ID helper
  const getRoleById = (roleId) => jobRoles.find(r => r.id === roleId);

  const getSchedulesForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return schedules.filter(s => s.date === dateStr);
  };

  // Get availability summary for a day
  const getAvailabilitySummaryForDay = (dayOfWeek) => {
    if (!showAvailability || !staffAvailability.length) return null;
    
    const available = [];
    const unavailable = [];
    
    staffAvailability.forEach(staffData => {
      const avail = staffData.availability?.find(a => a.day_of_week === dayOfWeek);
      if (avail?.is_available) {
        available.push({
          name: staffData.user_name,
          time: avail.start_time && avail.end_time 
            ? `${avail.start_time}-${avail.end_time}` 
            : 'All day'
        });
      } else if (avail && !avail.is_available) {
        unavailable.push(staffData.user_name);
      }
    });
    
    return { available, unavailable };
  };

  // Now lives inside AddShiftDialog component

  const locationName = selectedLocation?.location_name || 'Schedule';

  const handleTemplateDragStart = (e, template) => {
    setDraggingTemplate(template);
    e.dataTransfer.setData('template', JSON.stringify(template));
  };

  const handleDayDrop = (e, date) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData('template');
    if (!raw) return;
    try {
      const template = JSON.parse(raw);
      setSelectedDate(date);
      setFormData({
        user_id: '',
        start_time: template.start_time || '09:00',
        end_time:   template.end_time   || '17:00',
        role_id:    template.role_id    || '',
        template_id: template.id        || '',
      });
      setDialogOpen(true);
    } catch (_) {}
    setDraggingTemplate(null);
  };

  const handleEmployeeDrop = (e, date, userId) => {
    e.preventDefault();
    e.stopPropagation(); // don't bubble to CardContent (day drop → modal)
    const raw = e.dataTransfer.getData('template');
    if (!raw || !userId) return;
    try {
      const template = JSON.parse(raw);
      handleTemplateDrop(template, date, userId);
    } catch (_) {}
    setDraggingTemplate(null);
  };

  const _fmtShortDate = (date) => {
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[date.getMonth()]}${date.getDate()}`;
  };

  const baseFilename = useMemo(() => {
    const safe = locationName.replace(/[^a-zA-Z0-9]/g, '');
    const year = weekDates[0]?.getFullYear() ?? new Date().getFullYear();
    return `${safe}_Week_${_fmtShortDate(weekDates[0])}-${_fmtShortDate(weekDates[6])}_${year}`;
  }, [locationName, weekDates]);

  const handleExport = async (format) => {
    const element = exportRef.current;
    if (!element) return;
    setExporting(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 1400,
        windowWidth: 1400,
        logging: false,
      });

      if (format === 'png') {
        const link = document.createElement('a');
        link.download = `${baseFilename}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      } else {
        const imgData = canvas.toDataURL('image/png');
        const pxW = canvas.width;
        const pxH = canvas.height;
        const mmW = 297; // A4 landscape width
        const mmH = (pxH * mmW) / pxW;
        const orientation = mmH > mmW ? 'portrait' : 'landscape';
        const pdf = new jsPDF({ orientation, unit: 'mm', format: [mmW, mmH] });
        pdf.addImage(imgData, 'PNG', 0, 0, mmW, mmH);
        pdf.save(`${baseFilename}.pdf`);
      }
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Schedule
              <HelpTooltip text="Managers assign shifts to employees for each day of the week. Shifts can be dragged, edited, copied from previous weeks, and published for staff." learnMoreUrl="/help/schedule" />
            </h1>
            <p className="text-slate-500 mt-1">
              Weekly shift schedule
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap justify-end">
            {canManage && (
              <div className="flex items-center gap-2">
                <Switch
                  checked={showAvailability}
                  onCheckedChange={setShowAvailability}
                  data-testid="toggle-availability"
                />
                <Label className="text-sm text-slate-600 cursor-pointer" onClick={() => setShowAvailability(!showAvailability)}>
                  {showAvailability ? <Eye size={16} className="inline mr-1" /> : <EyeOff size={16} className="inline mr-1" />}
                  Availability
                </Label>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={handlePrevWeek} data-testid="prev-week-btn">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="px-4 py-2 bg-white border border-slate-200 rounded-md text-sm font-medium">
                {formatDate(weekDates[0])} - {formatDate(weekDates[6])}
              </div>
              <Button variant="outline" size="icon" onClick={handleNextWeek} data-testid="next-week-btn">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            {canManage && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex items-center gap-2"
                    disabled={exporting}
                    data-testid="export-schedule-btn"
                  >
                    {exporting
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Download className="h-4 w-4" />}
                    <span className="hidden sm:inline">Export</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuItem
                    onClick={() => handleExport('pdf')}
                    className="flex items-center gap-2 cursor-pointer"
                    data-testid="export-pdf-btn"
                  >
                    <FileText className="h-4 w-4 text-slate-500" />
                    Export as PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleExport('png')}
                    className="flex items-center gap-2 cursor-pointer"
                    data-testid="export-png-btn"
                  >
                    <FileImage className="h-4 w-4 text-slate-500" />
                    Export as PNG
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {canManage && (
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={() => setPrintDialogOpen(true)}
                data-testid="print-view-btn"
              >
                <Printer className="h-4 w-4" />
                <span className="hidden sm:inline">Print</span>
              </Button>
            )}
            {canManage && (
              <div className="flex items-center gap-2">
                {/* Bulk Create Button */}
                {shiftTemplates.length > 0 && staff.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenBulkDialog}
                    className="flex items-center gap-1.5"
                    data-testid="bulk-create-btn"
                  >
                    <CalendarPlus size={14} />
                    <span className="hidden sm:inline">Bulk</span>
                  </Button>
                )}
                {/* Copy Last Week Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCopyDialogOpen(true)}
                  className="flex items-center gap-1.5"
                  data-testid="copy-last-week-btn"
                >
                  <Copy size={14} />
                  <span className="hidden sm:inline">Copy Last Week</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRolesDialogOpen(true)}
                  className="flex items-center gap-1.5"
                  data-testid="manage-roles-btn"
                >
                  <Palette size={14} />
                  <span className="hidden sm:inline">Roles</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTemplatesDialogOpen(true)}
                  className="flex items-center gap-1.5"
                  data-testid="manage-templates-btn"
                >
                  <Clock size={14} />
                  <span className="hidden sm:inline">Templates</span>
                </Button>
                {!showTemplatePanel && shiftTemplates.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowTemplatePanel(true)}
                    className="flex items-center gap-1.5"
                    data-testid="show-templates-panel-btn"
                  >
                    <GripVertical size={14} />
                  </Button>
                )}
                {/* Weekly Template Buttons */}
                {canManage && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setSaveTplName(''); setSaveTplOpen(true); }}
                      className="flex items-center gap-1.5 border-slate-300 text-slate-700"
                      data-testid="save-week-template-btn"
                      title="Save this week as a reusable template"
                    >
                      <BookmarkPlus size={14} />
                      <span className="hidden sm:inline">Save Template</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { fetchWeeklyTemplates(); setLoadTplOpen(true); }}
                      className="flex items-center gap-1.5 border-slate-300 text-slate-700"
                      data-testid="load-week-template-btn"
                      disabled={weeklyTemplates.length === 0}
                      title={weeklyTemplates.length === 0 ? 'No templates saved yet' : 'Load a saved template'}
                    >
                      <FolderOpen size={14} />
                      <span className="hidden sm:inline">Load Template</span>
                    </Button>
                  </>
                )}
                {/* Publish Button */}
                {draftCount > 0 && (
                  <Button
                    size="sm"
                    onClick={() => setPublishDialogOpen(true)}
                    className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700"
                    data-testid="publish-schedule-btn"
                  >
                    <Send size={14} />
                    <span className="hidden sm:inline">Publish</span>
                    <span className="bg-white/20 px-1.5 py-0.5 rounded text-xs">{draftCount}</span>
                  </Button>
                )}
                {/* Unpublish Button */}
                {publishedCount > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleUnpublishSchedule}
                    className="flex items-center gap-1.5 border-amber-400 text-amber-700 hover:bg-amber-50"
                    data-testid="unpublish-schedule-btn"
                  >
                    <span className="hidden sm:inline">Unpublish</span>
                    <span className="bg-amber-100 px-1.5 py-0.5 rounded text-xs">{publishedCount}</span>
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Empty state — shown when no shifts for the week */}
        {schedules.length === 0 && (
          <Card className="border-slate-200" data-testid="schedule-empty-state">
            <CardContent className="py-14 text-center">
              <Calendar className="mx-auto h-10 w-10 text-slate-300 mb-3" />
              <p className="text-slate-500 font-medium text-base">No shifts this week</p>
              <p className="text-sm text-slate-400 mt-1 mb-6">
                {canManage ? 'Start by adding shifts to the schedule' : 'No shifts have been scheduled for this week yet'}
              </p>
              {canManage && (
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Button
                    onClick={() => handleAddShift(weekDates[0] || new Date())}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="schedule-empty-cta"
                  >
                    <Plus size={16} className="mr-1.5" />
                    Add Shift
                  </Button>
                  {shiftTemplates.length > 0 && staff.length > 0 && (
                    <Button
                      variant="outline"
                      onClick={handleOpenBulkDialog}
                      className="border-slate-300 text-slate-700"
                      data-testid="schedule-empty-bulk-btn"
                    >
                      <CalendarPlus size={16} className="mr-1.5" />
                      Bulk Create Shifts
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => setCopyDialogOpen(true)}
                    className="border-slate-300 text-slate-700"
                    data-testid="schedule-empty-copy-btn"
                  >
                    <Copy size={16} className="mr-1.5" />
                    Copy Last Week
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Weekly Calendar */}
        <TooltipProvider>
          <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
            {weekDates.map((date, index) => {
              const isToday = date.toDateString() === new Date().toDateString();
              const daySchedules = getSchedulesForDate(date);
              const availabilitySummary = getAvailabilitySummaryForDay(date.getDay());
              const dateIso = date.toISOString().split('T')[0];
              const holiday = holidaysByDate[dateIso];
              const isHoliday = !!holiday?.is_holiday;

              // Coverage Health — scheduled vs absent visibility for this
              // day. Derived purely from already-loaded data (zero extra API
              // calls). NOT a staffing-sufficiency prediction — this is
              // pure visibility until we ship a labor-demand forecaster.
              const dayLeaves = leaveByDate[dateIso] || [];
              const absentCount = new Set(dayLeaves.filter((l) => l.status === 'approved').map((l) => l.employee_id)).size;
              const scheduledCount = new Set(daySchedules.map((s) => s.user_id).filter(Boolean)).size;
              const totalStaff = staff.length;
              // Ratio band: absent / total_staff (falls back to scheduled+absent if no staff list)
              const denom = totalStaff || (scheduledCount + absentCount);
              const ratio = denom > 0 ? absentCount / denom : 0;
              const coverageBand = ratio === 0 ? 'green' : ratio < 0.1 ? 'green' : ratio < 0.25 ? 'amber' : 'red';
              const coverageCls = {
                green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                amber: 'bg-amber-50 text-amber-800 border-amber-200',
                red:   'bg-rose-50 text-rose-700 border-rose-200',
              }[coverageBand];

              return (
                <Card
                  key={index}
                  title={isHoliday ? `${holiday.holiday_name} — holiday pay rules may apply` : undefined}
                  className={`border-slate-200 ${isToday ? 'ring-2 ring-orange-500' : ''} ${draggingTemplate ? 'ring-1 ring-orange-300 ring-offset-1' : ''} ${isHoliday ? 'bg-amber-50/60 border-amber-200' : ''}`}
                  data-testid={`schedule-day-${dateIso}${isHoliday ? '-holiday' : ''}`}
                >
                  <CardHeader className="p-3 pb-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-500 uppercase">
                          {getShortDayName(date.getDay())}
                        </p>
                        <p className={`text-lg font-bold ${isToday ? 'text-orange-500' : 'text-slate-900'}`}>
                          {date.getDate()}
                        </p>
                        {isHoliday && (
                          <p
                            className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 border border-amber-200 max-w-full"
                            data-testid={`holiday-badge-${dateIso}`}
                          >
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
                            <span className="truncate max-w-[120px]">{holiday.holiday_name}</span>
                          </p>
                        )}
                        {/* Coverage Health — pure visibility, NOT a staffing prediction.
                            Computed from already-loaded shifts + leave data; zero extra
                            API calls. Shown only when there are scheduled shifts or
                            anyone on leave for this day. */}
                        {(scheduledCount > 0 || absentCount > 0) && (
                          <p
                            className={`mt-1 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium border ${coverageCls}`}
                            title={`${scheduledCount} scheduled · ${absentCount} on leave${totalStaff ? ` of ${totalStaff} staff` : ''}`}
                            data-testid={`coverage-stat-${dateIso}`}
                          >
                            <span className={`inline-block h-1.5 w-1.5 rounded-full ${coverageBand === 'green' ? 'bg-emerald-500' : coverageBand === 'amber' ? 'bg-amber-500' : 'bg-rose-500'}`} />
                            <span className="tabular-nums">{scheduledCount}</span>
                            <span className="opacity-60">scheduled</span>
                            {absentCount > 0 && (
                              <>
                                <span className="opacity-40">·</span>
                                <span className="tabular-nums">{absentCount}</span>
                                <span className="opacity-60">on leave</span>
                              </>
                            )}
                          </p>
                        )}
                      </div>
                      {canManage && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleAddShift(date)}
                          data-testid={`add-shift-${index}`}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent
                    className="p-3 pt-0 space-y-2"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleDayDrop(e, date)}
                  >
                    {/* Leave overlay — pure visual, never blocks scheduling.
                        Shows approved + pending leave covering this date so
                        managers see availability at a glance. */}
                    {(leaveByDate[dateIso] || []).length > 0 && (
                      <div className="space-y-1 -mt-1" data-testid={`leave-overlay-${dateIso}`}>
                        {(leaveByDate[dateIso] || []).slice(0, 4).map((lv) => (
                          <div
                            key={`${lv.id}-${dateIso}`}
                            title={`${lv.employee_name || 'Employee'} — ${lv.leave_type_name}${lv.status === 'pending' ? ' (pending approval)' : ''}`}
                            className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] leading-tight ${lv.status === 'pending' ? 'border border-dashed' : 'border'}`}
                            style={{
                              backgroundColor: `${lv.display_color || '#94A3B8'}1A`,
                              borderColor: `${lv.display_color || '#94A3B8'}66`,
                              color: '#1E293B',
                            }}
                            data-testid={`leave-pill-${lv.id}`}
                          >
                            <span
                              className="h-2 w-2 rounded-full shrink-0"
                              style={{ backgroundColor: lv.display_color || '#94A3B8' }}
                            />
                            <span className="font-medium truncate">{lv.employee_name || lv.leave_type_name}</span>
                            <span className="text-slate-500 ml-auto text-[10px] uppercase shrink-0">
                              {lv.status === 'pending' ? 'pending' : 'leave'}
                            </span>
                          </div>
                        ))}
                        {(leaveByDate[dateIso] || []).length > 4 && (
                          <div className="text-[10px] text-slate-500 pl-1">
                            +{(leaveByDate[dateIso] || []).length - 4} more on leave
                          </div>
                        )}
                      </div>
                    )}

                    {/* Per-employee drop zones — only visible while dragging a template */}
                    {draggingTemplate && canManage && staff.length > 0 && (
                      <div className="space-y-1 mb-2">
                        {staff.map(member => (
                          <div
                            key={member.id}
                            className="flex items-center justify-between px-2 py-1.5 rounded border border-dashed border-orange-300 bg-orange-50 text-xs cursor-copy select-none"
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => handleEmployeeDrop(e, date, member.id)}
                            data-testid={`emp-drop-${member.id}-${date.toISOString().split('T')[0]}`}
                          >
                            <span className="font-medium text-slate-700 truncate">{member.name}</span>
                            <span className="text-orange-400 text-[10px] ml-2 flex-shrink-0">+ assign</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Availability Overlay */}
                    {showAvailability && canManage && availabilitySummary && (
                      <div className="mb-2 p-2 bg-blue-50 rounded border border-blue-100 text-xs">
                        {availabilitySummary.available.length > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-1 text-green-700 cursor-help">
                                <CheckCircle size={12} />
                                <span>{availabilitySummary.available.length} available</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-xs">
                              <div className="space-y-1">
                                {availabilitySummary.available.map((a, i) => (
                                  <div key={i} className="text-xs">
                                    <strong>{a.name}</strong>: {a.time}
                                  </div>
                                ))}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {availabilitySummary.unavailable.length > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-1 text-red-700 cursor-help mt-1">
                                <XCircle size={12} />
                                <span>{availabilitySummary.unavailable.length} unavailable</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              <div className="text-xs">
                                {availabilitySummary.unavailable.join(', ')}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    )}

                    {daySchedules.length === 0 ? (
                      <p className="text-xs text-slate-400 text-center py-2">No shifts</p>
                    ) : (
                      daySchedules.map((schedule) => {
                        // Check if this shift has a conflict with availability
                        const availConflict = checkAvailabilityConflict(
                          schedule.user_id,
                          date,
                          schedule.start_time,
                          schedule.end_time
                        );
                        
                        // Get role color (from schedule or lookup)
                        const roleColor = schedule.role_color || (schedule.role_id ? getRoleById(schedule.role_id)?.color : null);
                        
                        return (
                          <div
                            key={schedule.id}
                            className={`p-2 rounded border group relative overflow-hidden ${
                              availConflict.hasConflict 
                                ? 'bg-amber-50 border-amber-200' 
                                : schedule.status === 'draft'
                                  ? 'bg-slate-100 border-slate-300 border-dashed'
                                  : 'bg-slate-50 border-slate-100'
                            }`}
                            style={roleColor ? { borderLeftWidth: '4px', borderLeftColor: roleColor } : {}}
                            data-testid={`schedule-${schedule.id}`}
                          >
                            {/* Draft badge */}
                            {schedule.status === 'draft' && (
                              <span
                                className="absolute top-0.5 right-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-400 bg-slate-200 px-1 py-0.5 rounded"
                                data-testid={`draft-badge-${schedule.id}`}
                              >
                                Draft
                              </span>
                            )}
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1">
                                  <p className="text-sm font-medium text-slate-900 truncate">
                                    {schedule.user_name}
                                  </p>
                                  {availConflict.hasConflict && (
                                    <Tooltip>
                                      <TooltipTrigger>
                                        <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p className="text-xs">{availConflict.reason}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                </div>
                                <p className="text-xs text-slate-500">
                                  {schedule.start_time} - {schedule.end_time}
                                </p>
                                {schedule.role_name && (
                                  <p className="text-xs font-medium mt-0.5" style={{ color: roleColor || '#64748b' }}>
                                    {schedule.role_name}
                                  </p>
                                )}
                                {schedule.template_name && (
                                  <p className="text-xs text-slate-400 italic">
                                    {schedule.template_name}
                                  </p>
                                )}
                              </div>
                              {canManage && (
                                <div className="flex items-center gap-0.5">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => setReplacementShiftId(schedule.id)}
                                    title="Find replacement"
                                    data-testid={`find-replacement-${schedule.id}`}
                                  >
                                    <UserCheck className="h-3 w-3 text-indigo-600" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => handleDeleteShift(schedule.id)}
                                  >
                                    <Trash2 className="h-3 w-3 text-red-500" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TooltipProvider>

        {/* ── Add Shift Dialog ── */}
        <AddShiftDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleSubmit}
          formData={formData}
          setFormData={setFormData}
          selectedDate={selectedDate}
          staff={staff}
          jobRoles={jobRoles}
          conflicts={conflicts}
          submitting={submitting}
          getStaffAvailabilityForDay={getStaffAvailabilityForDay}
          schedules={schedules}
        />

        {/* ── Replacement dialog (manager-initiated) ── */}
        <ReplacementDialog
          open={!!replacementShiftId}
          onOpenChange={(o) => !o && setReplacementShiftId(null)}
          shiftId={replacementShiftId}
        />

        {/* ── Templates Sidebar Panel ── */}
        <TemplatesSidebar
          shiftTemplates={shiftTemplates}
          show={canManage && showTemplatePanel}
          onClose={() => setShowTemplatePanel(false)}
          onDragStart={handleTemplateDragStart}
          onDragEnd={() => setDraggingTemplate(null)}
        />

        {/* ── Job Roles Management Dialog ── */}
        <RolesDialog
          open={rolesDialogOpen}
          onClose={() => setRolesDialogOpen(false)}
          jobRoles={jobRoles}
          roleFormData={roleFormData}
          setRoleFormData={setRoleFormData}
          editingRole={editingRole}
          setEditingRole={setEditingRole}
          onSave={handleSaveRole}
          onDelete={handleDeleteRole}
        />

        {/* ── Publish Schedule Confirmation Dialog ── */}
        <PublishDialog
          open={publishDialogOpen}
          onClose={() => setPublishDialogOpen(false)}
          onPublish={handlePublishSchedule}
          draftCount={draftCount}
          weekDates={weekDates}
          sendNotifications={sendNotifications}
          setSendNotifications={setSendNotifications}
          publishing={publishing}
        />

        {/* ── Bulk Create Shifts Dialog ── */}
        <BulkCreateDialog
          open={bulkDialogOpen}
          onClose={() => setBulkDialogOpen(false)}
          onSubmit={handleBulkCreate}
          shiftTemplates={shiftTemplates}
          staff={staff}
          weekDates={weekDates}
          bulkFormData={bulkFormData}
          setBulkFormData={setBulkFormData}
          creatingBulk={creatingBulk}
          toggleUser={toggleBulkUser}
          toggleDate={toggleBulkDate}
          selectAllStaff={selectAllStaff}
          selectAllDates={selectAllWeekDates}
        />

        {/* ── Shift Templates Management Dialog ── */}
        <ShiftTemplatesDialog
          open={templatesDialogOpen}
          onClose={() => setTemplatesDialogOpen(false)}
          shiftTemplates={shiftTemplates}
          jobRoles={jobRoles}
          templateFormData={templateFormData}
          setTemplateFormData={setTemplateFormData}
          editingTemplate={editingTemplate}
          setEditingTemplate={setEditingTemplate}
          onSave={handleSaveTemplate}
          onDelete={handleDeleteTemplate}
        />

        {/* ── Copy Last Week Dialog ── */}
        <CopyLastWeekDialog
          open={copyDialogOpen}
          onClose={() => setCopyDialogOpen(false)}
          onCopy={handleCopyLastWeek}
          copying={copyingLastWeek}
          weekDates={weekDates}
        />
      </div>

      {/* ── Hidden export container (off-screen, always rendered for html2canvas) ── */}
      <div
        ref={exportRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: '-9999px',
          left: 0,
          width: '1400px',
          background: '#ffffff',
          padding: '40px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          zIndex: -1,
        }}
      >
        {/* Export Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px', paddingBottom: '20px', borderBottom: '2px solid #f97316' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ background: '#f97316', borderRadius: '8px', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ color: '#fff', fontWeight: '800', fontSize: '15px', fontFamily: 'Arial, sans-serif' }}>RS</span>
            </div>
            <span style={{ fontWeight: '700', fontSize: '22px', color: '#0f172a', letterSpacing: '-0.3px' }}>ReStaff</span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a' }}>{locationName}</div>
            <div style={{ fontSize: '14px', color: '#64748b', marginTop: '4px' }}>
              Week of {formatDate(weekDates[0])} – {formatDate(weekDates[6])}
            </div>
          </div>
        </div>

        {/* Schedule Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '10px' }}>
          {weekDates.map((date, index) => {
            const isToday = date.toDateString() === new Date().toDateString();
            const daySchedules = getSchedulesForDate(date);
            return (
              <div
                key={index}
                style={{
                  border: `1px solid ${isToday ? '#f97316' : '#e2e8f0'}`,
                  borderRadius: '10px',
                  overflow: 'hidden',
                  boxShadow: isToday ? '0 0 0 2px #f97316' : 'none',
                }}
              >
                {/* Day column header */}
                <div style={{ background: isToday ? '#fff7ed' : '#f8fafc', padding: '12px 14px', borderBottom: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em' }}>
                    {getShortDayName(date.getDay())}
                  </div>
                  <div style={{ fontSize: '26px', fontWeight: '800', color: isToday ? '#f97316' : '#0f172a', lineHeight: 1.1, marginTop: '2px' }}>
                    {date.getDate()}
                  </div>
                </div>
                {/* Shifts */}
                <div style={{ padding: '10px', minHeight: '90px', background: '#ffffff' }}>
                  {daySchedules.length === 0 ? (
                    <div style={{ color: '#cbd5e1', fontSize: '12px', textAlign: 'center', paddingTop: '20px' }}>
                      No shifts
                    </div>
                  ) : (
                    daySchedules.map((schedule) => {
                      const roleColor = schedule.role_color || (schedule.role_id ? getRoleById(schedule.role_id)?.color : null);
                      return (
                        <div
                          key={schedule.id}
                          style={{
                            background: roleColor ? `${roleColor}15` : '#f1f5f9',
                            border: '1px solid #e2e8f0',
                            borderLeft: roleColor ? `4px solid ${roleColor}` : '1px solid #e2e8f0',
                            borderRadius: '6px',
                            padding: '7px 10px',
                            marginBottom: '6px',
                          }}
                        >
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {schedule.user_name}
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                            {schedule.start_time} – {schedule.end_time}
                          </div>
                          {schedule.role_name && (
                            <div style={{ fontSize: '10px', fontWeight: '600', color: roleColor || '#64748b', marginTop: '2px' }}>
                              {schedule.role_name}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Export Footer */}
        <div style={{ marginTop: '28px', paddingTop: '14px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#94a3b8' }}>
          <span>Generated by ReStaff · getrestaff.co</span>
          <span>Exported on {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
        </div>
      </div>

      {/* PrintableSchedule overlay */}
      {canManage && (
        <PrintableSchedule
          open={printDialogOpen}
          onClose={() => setPrintDialogOpen(false)}
          schedules={schedules}
          staff={staff}
          weekDates={weekDates}
          locationName={locationName}
        />
      )}

      {/* Save Week as Template Dialog */}
      <Dialog open={saveTplOpen} onOpenChange={setSaveTplOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Save Week as Template</DialogTitle>
            <DialogDescription>
              Save all shifts for the week of {weekStart.toLocaleDateString()} as a reusable template.
              Employee assignments will not be saved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label htmlFor="tpl-name">Template Name</Label>
              <Input
                id="tpl-name"
                placeholder={`Week of ${weekStart.toISOString().split('T')[0]}`}
                value={saveTplName}
                onChange={(e) => setSaveTplName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSaveWeekAsTemplate()}
                data-testid="save-template-name-input"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveTplOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSaveWeekAsTemplate}
              disabled={!saveTplName.trim() || savingTpl}
              data-testid="save-template-submit-btn"
            >
              {savingTpl ? 'Saving…' : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Load Template Dialog */}
      <Dialog open={loadTplOpen} onOpenChange={setLoadTplOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Load Schedule Template</DialogTitle>
            <DialogDescription>
              Applying a template will overwrite all draft shifts for the week of {weekStart.toLocaleDateString()}.
              Published shifts will not be affected.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {weeklyTemplates.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6" data-testid="templates-empty">
                No templates saved yet. Use "Save Template" to create one.
              </p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="templates-list">
                {weeklyTemplates.map((tpl) => (
                  <div
                    key={tpl.id}
                    className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50"
                    data-testid={`template-row-${tpl.id}`}
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-slate-900 truncate">{tpl.name}</p>
                      <p className="text-xs text-slate-500">
                        {tpl.shift_count} shifts · {new Date(tpl.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2 ml-3 flex-shrink-0">
                      <Button
                        size="sm"
                        onClick={() => handleLoadWeekTemplate(tpl.id)}
                        disabled={loadingTpl === tpl.id}
                        data-testid={`load-template-btn-${tpl.id}`}
                      >
                        {loadingTpl === tpl.id ? 'Loading…' : 'Apply'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleDeleteWeekTemplate(tpl.id)}
                        data-testid={`delete-template-btn-${tpl.id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoadTplOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
